import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { LockClosedIcon } from '@heroicons/react/24/outline';

interface FeatureUpgradeNoticeProps {
  /** Human-readable feature name, e.g. "Gamification" or "Prize Management". */
  feature: string;
  /** Optional one-line explanation of what the feature does. */
  description?: string;
  /** The tenant's current plan, surfaced so the user knows where they stand. */
  currentPlan?: string | null;
  /** Optional custom icon; defaults to a lock. */
  icon?: React.ReactNode;
}

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

/**
 * Inline "upgrade your plan" announcement shown in place of a tier-locked
 * feature's content. Used on the Gamification and Prize Management surfaces so
 * the nav entries stay visible on every plan, but the feature itself prompts an
 * upgrade when the tenant's subscription doesn't include it.
 */
const FeatureUpgradeNotice: React.FC<FeatureUpgradeNoticeProps> = ({
  feature,
  description,
  currentPlan,
  icon,
}) => {
  const planLabel = currentPlan ? PLAN_LABELS[currentPlan] ?? currentPlan : null;

  return (
    <motion.div {...slideUp} className="flex justify-center px-4 py-16">
      <div
        role="status"
        className="card-elevated flex max-w-md flex-col items-center gap-4 rounded-3xl p-10 text-center"
      >
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-350">
          {icon ?? <LockClosedIcon className="h-8 w-8" />}
        </div>
        <h2 className="text-xl font-bold text-text-primary">
          {feature} isn’t available on your plan
        </h2>
        <div className="flex max-w-sm flex-col gap-3">
          {description && <p className="text-sm text-text-secondary">{description}</p>}
          <p className="text-sm text-text-secondary">
            Upgrade to <span className="font-semibold text-text-primary">Pro</span> or{' '}
            <span className="font-semibold text-text-primary">Enterprise</span> to unlock{' '}
            {feature}.
            {planLabel ? ` You are currently on the ${planLabel} plan.` : ''}
          </p>
        </div>
        <Link
          to="/settings?tab=billing"
          className="btn-primary mt-2 inline-flex items-center gap-2 active:scale-[0.97]"
        >
          View plans &amp; upgrade
        </Link>
      </div>
    </motion.div>
  );
};

export default FeatureUpgradeNotice;
